import { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  CheckCircle2,
  ChevronRight,
  ShieldCheck,
  Home,
  Brain,
  Sparkles,
  Activity,
  FileText,
  ArrowRight,
  Info,
} from "lucide-react";
import SEOHead from "@/components/SEOHead";
import {
  RelatedPages,
  allTreatmentsLinks,
  allServiceLinks,
  allDecisionLinks,
} from "@/components/RelatedPages";
const whyProliv = [
  {
    title: "A proven next-step therapy",
    desc: "For those who haven’t adequately improved with medication alone",
    icon: Sparkles,
  },
  {
    title: "Treatment at home",
    desc: "No clinics or waiting rooms - use it at the privacy of your home",
    icon: Home,
  },
  {
    title: "Advanced brain neuromodulation",
    desc: "Targets key brain pathways linked to depression",
    icon: Brain,
  },
  {
    title: "Clinically demonstrated",
    desc: "Meaningful improvement in depression symptoms",
    icon: Activity,
  },
  {
    title: "Adjunctive therapy",
    desc: "Enhances the effects of your current antidepressant treatment",
    icon: CheckCircle2,
  },
  {
    title: "Fits into daily life",
    desc: "Use while resting or watching TV",
    icon: ShieldCheck,
  },
  {
    title: "Physician-guided care",
    desc: "Prescribed and monitored by your doctor",
    icon: FileText,
  },
  {
    title: "Safe and drug-free",
    desc: "Non-invasive and well-tolerated",
    icon: ShieldCheck,
  },
];
const journeySteps = [
  {
    step: "01",
    title: "Talk with your physician",
    iconUrl: "https://prolivrx.com/wp-content/uploads/2026/04/icon_pat_1.svg",
    desc: "Your physician will review your symptoms and treatment history to determine whether Proliv®Rx is appropriate for you. If prescribed, they will guide you through what to expect and address any questions you may have.",
  },
  {
    step: "02",
    title: "Get started in the clinic or at home",
    iconUrl: "https://prolivrx.com/wp-content/uploads/2026/04/icon_pat_2.svg",
    desc: "You’ll receive everything you need to begin treatment at home, with clear instructions and support through the app and Proliv®Rx Customer Care to help you get set up comfortably.",
  },
  {
    step: "03",
    title: "Complete guided daily sessions",
    iconUrl: "https://prolivrx.com/wp-content/uploads/2026/04/icon_pat_3.svg",
    desc: "You’ll begin with a structured one-week NeuroPrime Phase to help your nervous system gradually adapt to the therapy. From there, you’ll move into the eight-week NeuroRestore Phase, with two 40-minute, guided sessions each day that fit into your daily routine.",
  },
  {
    step: "04",
    title: "Ongoing monitoring by your physician",
    iconUrl: "https://prolivrx.com/wp-content/uploads/2026/04/icon_pat_4.svg",
    desc: "Throughout the treatment, your physician stays connected to your progress, helping guide next steps and make adjustments as needed.",
  },
  {
    step: "05",
    title: "Review progress and next steps",
    iconUrl: "https://prolivrx.com/wp-content/uploads/2026/04/icon_pat_5.svg",
    desc: "At the end of treatment, you and your physician will review how you’re feeling and decide what comes next. Based on your progress, your physician may recommend additional Proliv®Rx therapy sessions to further optimize your treatment outcome.",
  },
];
const psychiatristInsights = [
  {
    quote:
      "This effective and highly accessible therapy with a favorable safety profile, addresses a significant unmet need in this underserved patient population and expands how we can deliver care.",
    role: "Professor of Psychiatry, Radiology and Neurosciences and Director of the Brain Stimulation Division at the Medical University of South Carolina (MUSC)",
    avatar: "https://prolivrx.com/wp-content/uploads/2026/03/ava.png",
    name: "Mark George, MD",
  },
  {
    quote:
      "Proliv®Rx offers an accessible and effective solution for patients whose depression is unresponsive to medications, many of whom have endured years of persistent symptoms. This therapy has the potential to become an essential tool in the mental health treatment landscape.",
    role: "Professor of Psychiatry at Brown University and Chief of the Mood Disorder Program at Butler Hospital",
    avatar: "https://prolivrx.com/wp-content/uploads/2026/03/ava2.png",
    name: "Linda Carpenter, MD",
  },
  {
    quote:
      "Never before has there been an FDA-cleared treatment option for difficult-to-treat depression that is safe, effective, and can be used privately in the patient's home - expanding access to therapy for this underserved population. Proliv Rx just treats depression - without the hassle.",
    role: "Chief Medical Officer, Neurolief. Dual board-certified psychiatrist and leading researcher in neuromodulation for treatment-resistant mood disorders.",
    avatar: "https://prolivrx.com/wp-content/uploads/2026/03/owenscott.png",
    name: "Owen Scott Muir, MD",
  },
];
const faqCategories = [
  {
    category: "Who is Proliv®Rx for",
    items: [
      {
        q: "What if I've tried many antidepressant medications and it didn't help me enough?",
        a: "Proliv®Rx is an FDA-cleared neuromodulation therapy for adults whose depression hasn’t responded sufficiently to antidepressant medication. It is designed to be used at home under physician guidance.",
      },
      {
        q: "What if I’ve tried many antidepressant medications and it didn’t help me enough?",
        a: "That’s exactly who this therapy was designed for. Proliv®Rx is an adjunctive therapy intended to enhance the effects of your current antidepressant treatment.",
      },
    ],
  },
  {
    category: "Proliv®Rx therapy",
    items: [
      {
        q: "How long is the therapy program?",
        a: "The core program is 8 weeks, with two daily sessions (about 40 minutes each). Your physician may extend it for another 8 weeks to maximize your clinical outcome.",
      },
      {
        q: "When do patients typically begin feeling better?",
        a: "Users usually start noticing improvement around weeks 4-6 of the therapy program.",
      },
      {
        q: "Do I need to stop my medications?",
        a: "No. You keep your current treatment unless your doctor tells you otherwise.",
      },
      {
        q: "Will I feel anything during treatment?",
        a: "Most patients describe the sensation as mild tingling over the scalp. It’s designed to be comfortable and pain-free.",
      },
      {
        q: "Is Proliv®Rx safe?",
        a: "Yes. In clinical studies, Proliv®Rx demonstrated a favorable safety profile. The majority of adverse events were mild to moderate and transient.",
      },
      {
        q: "Can I use it at home by myself?",
        a: "It’s used at home, following a prescription from your physician, who oversees your care.",
      },
      {
        q: "Does my physician stay involved?",
        a: "Yes. Your physician reviews your progress and decides whether you should continue, extend, or adjust treatment.",
      },
      {
        q: "Is this the same as TMS?",
        a: "No. TMS (Transcranial Magnetic Stimulation) is a different technology and requires in-clinic visits and specialized equipment. Proliv®Rx is an at-home prescription neuromodulation device.",
      },
      {
        q: "What if I miss a session?",
        a: "Just do the next one. Staying consistent matters, and the Proliv®Rx companion app helps keep you on track.",
      },
      {
        q: "Can I drive or work after a session?",
        a: "Yes. There is no downtime or recovery period needed.",
      },
    ],
  },
  {
    category: "Insurance Coverage",
    items: [
      {
        q: "Is it covered by insurance?",
        a: "Coverage varies depending on your insurance carrier and benefit plan. Our care team at Neuro Wellness can help verify your benefits and review coverage options.",
      },
    ],
  },
];
export default function ProlivRx() {
  const [activeStep, setActiveStep] = useState(0);
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={"Proliv®Rx At-Home TMS | Neuro Wellness TMS"}
        description={
          "Proliv®Rx is an FDA-cleared, at-home neuromodulation therapy for treatment-resistant depression. Learn how this personalized, non-invasive treatment works and request a consultation at Neuro Wellness in Coral Springs, FL."
        }
        canonical={"/prolivrx"}
        keywords={
          "Proliv Rx at-home neuromodulation, depression treatment device, FDA-cleared neuromodulation, treatment-resistant depression home therapy, non-invasive brain stimulation Coral Springs"
        }
        schemaJson={{
          "@context": "https://schema.org",
          "@type": "MedicalTherapy",
          name: "Proliv®Rx At-Home Neuromodulation",
          description:
            "FDA-cleared at-home neuromodulation therapy for depression, personalized to each patient's brain activity.",
          url: "/prolivrx",
          procedureType: "Transcranial Electrical Stimulation",
          relevantSpecialty: "Psychiatry",
          provider: {
            "@id": "https://neurowellnesstms.com/#organization",
          },
        }}
      />

      <Navbar />

      <main className="pt-16 sm:pt-20">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden bg-gradient-navy py-16 lg:py-24 text-white">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(47,111,159,0.25),transparent_60%)]" />
          <div className="container-px mx-auto relative z-10 max-w-7xl">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7 space-y-6">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs sm:text-sm font-medium backdrop-blur-md">
                  <Sparkles className="h-4 w-4 text-accent" />
                  <span>FDA-Cleared At-Home Neuromodulation</span>
                </div>
                <h1 className="font-headings text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
                  A new path to depression relief{" "}
                  <span className="text-accent">beyond medication</span>
                </h1>

                <div className="flex flex-wrap gap-3 pt-2">
                  {["Clinically proven", "Non-invasive", "Used at home"].map(
                    (tag) => (
                      <span
                        key={tag}
                        className="rounded-md bg-white/10 border border-white/15 px-3 py-1.5 text-xs sm:text-sm font-semibold text-light-blue"
                      >
                        {tag}
                      </span>
                    ),
                  )}
                </div>

                <p className="text-base sm:text-lg text-light-blue/90 leading-relaxed max-w-2xl">
                  Proliv®Rx is a prescription-based, FDA-cleared neuromodulation
                  therapy for adults whose depression hasn’t responded
                  sufficiently to medication. It is designed for use at home,
                  under physician guidance.
                </p>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-4">
                  <Button
                    asChild
                    size="lg"
                    className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90 text-base font-semibold px-8 py-6"
                  >
                    <a href="/#consultation">Request Proliv®Rx Consultation</a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    size="lg"
                    className="border-2 border-white/50 bg-transparent text-white hover:bg-white/15 hover:border-white/70 text-base font-medium px-6 py-6"
                  >
                    <a href="#how-it-works">How It Works</a>
                  </Button>
                </div>
              </div>

              <div className="lg:col-span-5 relative">
                <div className="relative rounded-2xl overflow-hidden border border-white/20 shadow-2xl bg-primary-deep/50 backdrop-blur-sm">
                  <img
                    src="https://prolivrx.com/wp-content/uploads/2026/04/home-patients.jpg"
                    alt="Proliv Rx at home neuromodulation device"
                    className="w-full h-auto object-cover rounded-2xl transform transition-transform hover:scale-105 duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-deep/80 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 p-4 rounded-xl bg-primary/90 border border-white/10 backdrop-blur-md text-xs sm:text-sm text-light-blue">
                    <p className="font-semibold text-white">
                      NeuroRouter At-Home Headset
                    </p>
                    <p className="text-muted-foreground mt-0.5">
                      Gentle, targeted nerve pathways stimulation under medical
                      supervision.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHY PROLIV RX */}
        <section className="bg-primary-deep text-white py-16 lg:py-24 border-t border-white/10">
          <div className="container-px mx-auto max-w-7xl">
            <div className="text-center max-w-3xl mx-auto mb-14">
              <span className="text-accent font-semibold tracking-wider uppercase text-xs sm:text-sm">
                At-Home Relief
              </span>
              <h2 className="font-headings text-3xl sm:text-4xl font-bold mt-2 text-white">
                Why Choose Proliv®Rx?
              </h2>
              <p className="text-light-blue/80 mt-3 text-base sm:text-lg">
                If you’re struggling with depression that hasn’t improved with
                medication alone, Proliv®Rx offers a new path forward.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {whyProliv.map((item, idx) => {
                const IconComp = item.icon;
                return (
                  <div
                    key={idx}
                    className="p-6 rounded-2xl bg-primary/40 border border-white/10 hover:border-accent/50 transition-all hover:bg-primary/70 group"
                  >
                    <div className="h-12 w-12 rounded-xl bg-accent/20 border border-accent/30 flex items-center justify-center text-accent mb-4 group-hover:scale-110 transition-transform">
                      <IconComp className="h-6 w-6" />
                    </div>
                    <h3 className="font-headings text-lg font-bold text-white mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-light-blue/80 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section
          id="how-it-works"
          className="bg-background py-16 lg:py-24 border-b border-border"
        >
          <div className="container-px mx-auto max-w-7xl">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-6 space-y-6">
                <span className="text-accent font-semibold tracking-wider uppercase text-xs sm:text-sm">
                  Advanced Neuromodulation
                </span>
                <h2 className="font-headings text-3xl sm:text-4xl font-bold text-primary">
                  How Proliv®Rx Works
                </h2>
                <div className="space-y-4 text-foreground/80 leading-relaxed text-sm sm:text-base">
                  <p>
                    Your brain is a network of more than 80 billion neurons that
                    communicate using tiny electrical signals. These signals
                    share information across different regions of the brain.
                  </p>
                  <p>
                    In depression, communication between regions becomes
                    disrupted, affecting mood and emotional well-being.
                  </p>
                  <p className="font-medium text-primary bg-light-blue/30 p-4 rounded-xl border border-light-blue">
                    Proliv®Rx is designed to improve communication and
                    connectivity across the brain. Using advanced
                    neuromodulation, it harnesses the brain’s natural capacity
                    to change, helping build healthier neural connections.
                  </p>
                  <p>
                    With its wearable headset called the NeuroRouter, Proliv®Rx
                    delivers gentle electrical signals through electrodes
                    (NeuroPorts) placed on the forehead and the back of the
                    head.
                  </p>
                  <p>
                    Much like how a router directs signals in a network, the
                    NeuroRouter helps guide these signals precisely and
                    non-invasively along natural nerve pathways to key regions
                    involved in depression.
                  </p>
                  <p className="font-bold text-primary text-base sm:text-lg">
                    With consistent use, Proliv®Rx helps restore balance in your
                    brain, so you can feel better again.
                  </p>
                </div>
              </div>

              <div className="lg:col-span-6">
                <div className="rounded-2xl border border-border bg-card p-6 shadow-premium">
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    className="w-full rounded-xl bg-primary-deep/90 object-cover border border-border aspect-video"
                  >
                    <source
                      src="https://prolivrx.com/wp-content/uploads/2026/04/Animation-4.mp4"
                      type="video/mp4"
                    />
                    Your browser does not support the video tag.
                  </video>
                  <div className="mt-4 p-4 rounded-xl bg-light-blue/20 border border-light-blue text-xs sm:text-sm text-foreground">
                    <p className="font-semibold text-primary">
                      NeuroRouter Technology
                    </p>
                    <p className="text-muted-foreground mt-0.5">
                      Gentle electrical signals target trigeminal and occipital
                      nerve pathways non-invasively.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* WHAT IT FEELS LIKE */}
            <div className="mt-16 rounded-2xl bg-gradient-navy text-white p-8 lg:p-12 border border-white/10 shadow-premium">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-5">
                  <img
                    src="https://prolivrx.com/wp-content/uploads/2026/05/What-it-feels-like-section.png"
                    alt="What Proliv Rx feels like"
                    className="rounded-xl w-full h-auto object-cover border border-white/20"
                  />
                </div>
                <div className="lg:col-span-7 space-y-4">
                  <h3 className="font-headings text-2xl sm:text-3xl font-bold text-white">
                    What Does It Feel Like?
                  </h3>
                  <p className="text-light-blue/90 text-sm sm:text-base leading-relaxed">
                    Proliv®Rx therapy is designed to be{" "}
                    <strong className="text-white">
                      comfortable and pain-free
                    </strong>
                    . Most people describe the sensation as a gentle tingling on
                    the scalp.
                  </p>
                  <p className="text-light-blue/90 text-sm sm:text-base leading-relaxed">
                    During treatment, you remain fully awake, comfortable, and
                    able to resume normal daily activities immediately
                    afterward.
                  </p>
                  <div className="p-4 rounded-xl bg-white/10 border border-white/15 text-xs sm:text-sm text-white font-medium">
                    Portable and travel-friendly, Proliv®Rx is designed to
                    integrate easily into everyday life and support treatment
                    continuity wherever you are.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TREATMENT JOURNEY */}
        <section className="bg-primary text-white py-16 lg:py-24">
          <div className="container-px mx-auto max-w-7xl">
            <div className="text-center max-w-3xl mx-auto mb-14">
              <span className="text-accent font-semibold tracking-wider uppercase text-xs sm:text-sm">
                Step-by-Step Care
              </span>
              <h2 className="font-headings text-3xl sm:text-4xl font-bold mt-2 text-white">
                What Your Treatment Journey Looks Like
              </h2>
              <p className="text-light-blue/80 mt-3 text-base">
                A structured, physician-guided process designed to fit
                seamlessly into your life.
              </p>
            </div>

            {/* STEP SELECTOR */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 mb-8">
              {journeySteps.map((s, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveStep(idx)}
                  className={`p-3 sm:p-4 rounded-xl border text-left transition-all ${activeStep === idx ? "bg-accent text-accent-foreground border-accent font-bold shadow-glow" : "bg-primary-deep/50 border-white/15 text-light-blue hover:bg-primary-deep"}`}
                >
                  <div className="text-[10px] sm:text-xs font-bold opacity-80">
                    STEP {s.step}
                  </div>
                  <div className="text-xs sm:text-sm font-semibold leading-tight mt-1 line-clamp-2">
                    {s.title}
                  </div>
                </button>
              ))}
            </div>

            {/* ACTIVE STEP CARD */}
            <div className="rounded-2xl bg-primary-deep border border-white/15 p-8 lg:p-12 relative overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-8 space-y-4">
                  <div className="inline-flex items-center gap-2 rounded-full bg-accent/20 border border-accent/40 px-3 py-1 text-xs font-bold text-accent">
                    STEP {journeySteps[activeStep].step} OF 05
                  </div>
                  <h3 className="font-headings text-2xl sm:text-3xl font-bold text-white">
                    {journeySteps[activeStep].title}
                  </h3>
                  <p className="text-light-blue/90 text-base sm:text-lg leading-relaxed">
                    {journeySteps[activeStep].desc}
                  </p>
                  <div className="pt-4 flex items-center gap-3">
                    <Button
                      onClick={() =>
                        setActiveStep((prev) => (prev > 0 ? prev - 1 : 4))
                      }
                      variant="outline"
                      size="sm"
                      className="border-2 border-white/50 bg-transparent text-white hover:bg-white/15 hover:border-white/70"
                    >
                      Previous Step
                    </Button>
                    <Button
                      onClick={() =>
                        setActiveStep((prev) => (prev < 4 ? prev + 1 : 0))
                      }
                      size="sm"
                      className="bg-accent text-accent-foreground hover:opacity-90"
                    >
                      Next Step <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>

                <div className="lg:col-span-4 flex justify-center">
                  <div className="h-32 w-32 sm:h-40 sm:w-40 rounded-full bg-primary/60 border-2 border-accent/40 flex items-center justify-center p-6 shadow-glow">
                    <img
                      src={journeySteps[activeStep].iconUrl}
                      alt={journeySteps[activeStep].title}
                      className="h-16 w-16 sm:h-20 sm:w-20 object-contain invert filter"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* EFFICACY & SAFETY */}
        <section className="bg-background py-16 lg:py-24 border-b border-border">
          <div className="container-px mx-auto max-w-7xl">
            <div className="text-center max-w-3xl mx-auto mb-14">
              <span className="text-accent font-semibold tracking-wider uppercase text-xs sm:text-sm">
                Clinical Efficacy & Safety
              </span>
              <h2 className="font-headings text-3xl sm:text-4xl font-bold mt-2 text-primary">
                Proven Results & Safety Profile
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* DOES IT WORK CARD */}
              <div className="rounded-2xl border border-border bg-card p-8 shadow-soft flex flex-col justify-between">
                <div>
                  <div className="h-12 w-12 rounded-xl bg-accent/15 text-accent flex items-center justify-center mb-6">
                    <Activity className="h-6 w-6" />
                  </div>
                  <h3 className="font-headings text-2xl font-bold text-primary mb-4">
                    Does It Work?
                  </h3>
                  <div className="space-y-4 text-foreground/80 leading-relaxed text-sm sm:text-base">
                    <p className="text-base sm:text-lg font-medium text-primary">
                      In the Proliv®Rx clinical study,{" "}
                      <span className="text-accent font-bold">
                        87% of patients
                      </span>{" "}
                      with prior inadequate response to antidepressant
                      medications improved by one or more depression severity
                      levels.
                    </p>
                    <ul className="space-y-2 pt-2">
                      <li className="flex items-center gap-3 text-foreground">
                        <CheckCircle2 className="h-5 w-5 text-accent shrink-0" />
                        <span>
                          <strong>56%</strong> reached mild or no depression
                        </span>
                      </li>
                      <li className="flex items-center gap-3 text-foreground">
                        <CheckCircle2 className="h-5 w-5 text-accent shrink-0" />
                        <span>
                          <strong>31.7%</strong> achieved full remission
                        </span>
                      </li>
                    </ul>
                    <p className="text-xs text-muted-foreground italic pt-2">
                      Results after 16 weeks of therapy program completion.
                    </p>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-border">
                  <a
                    href="https://www.brainstimjrnl.com/article/S1935-861X(25)00315-8/fulltext"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-accent transition-colors"
                  >
                    Read Full Study Publication{" "}
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
              </div>

              {/* IS IT SAFE CARD */}
              <div className="rounded-2xl border border-border bg-card p-8 shadow-soft flex flex-col justify-between">
                <div>
                  <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-6">
                    <ShieldCheck className="h-6 w-6" />
                  </div>
                  <h3 className="font-headings text-2xl font-bold text-primary mb-4">
                    Is It Safe?
                  </h3>
                  <ul className="space-y-4 text-foreground/80 text-sm sm:text-base leading-relaxed">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                      <span>
                        <strong>FDA-Cleared:</strong> Met FDA requirements for
                        safety and effectiveness through the 510(k) clearance
                        pathway.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                      <span>
                        <strong>Non-Invasive:</strong> Generally mild to
                        moderate, transient side effects (e.g., mild scalp
                        tingling).
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                      <span>
                        <strong>Data-Driven Progress:</strong> Reports are
                        shared with your prescribing physician to support
                        ongoing progress monitoring and care adjustments.
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="mt-8 pt-6 border-t border-border">
                  <div className="p-4 rounded-xl bg-light-blue/20 border border-light-blue text-xs text-muted-foreground flex items-center gap-3">
                    <Info className="h-5 w-5 text-primary shrink-0" />
                    <span>
                      Non-invasive, medication-free adjunct therapy that
                      integrates smoothly into daily life.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PSYCHIATRIST INSIGHTS */}
        <section className="bg-primary-deep text-white py-16 lg:py-24">
          <div className="container-px mx-auto max-w-7xl">
            <div className="text-center max-w-3xl mx-auto mb-14">
              <span className="text-accent font-semibold tracking-wider uppercase text-xs sm:text-sm">
                Expert Endorsements
              </span>
              <h2 className="font-headings text-3xl sm:text-4xl font-bold mt-2 text-white">
                What Leading Psychiatrists Are Saying
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {psychiatristInsights.map((insight, idx) => (
                <div
                  key={idx}
                  className="rounded-2xl bg-primary/50 border border-white/10 p-8 flex flex-col justify-between hover:border-accent/40 transition-colors"
                >
                  <p className="text-sm sm:text-base text-light-blue/90 italic leading-relaxed mb-6">
                    "{insight.quote}"
                  </p>
                  <div className="pt-6 border-t border-white/10 flex items-center gap-4">
                    <img
                      src={insight.avatar}
                      alt={insight.name}
                      className="h-14 w-14 rounded-full object-cover border-2 border-accent"
                    />
                    <div>
                      <h4 className="font-headings text-base font-bold text-white">
                        {insight.name}
                      </h4>
                      <p className="text-xs text-light-blue/70 line-clamp-3 leading-tight mt-1">
                        {insight.role}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ ACCORDION */}
        <section className="bg-background py-16 lg:py-24">
          <div className="container-px mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <span className="text-accent font-semibold tracking-wider uppercase text-xs sm:text-sm">
                Answers to Your Questions
              </span>
              <h2 className="font-headings text-3xl sm:text-4xl font-bold mt-2 text-primary">
                Frequently Asked Questions
              </h2>
            </div>

            <div className="space-y-8">
              {faqCategories.map((cat, cIdx) => (
                <div key={cIdx} className="space-y-4">
                  <h3 className="font-headings text-xl font-bold text-primary border-b border-border pb-2">
                    {cat.category}
                  </h3>
                  <Accordion type="single" collapsible className="space-y-3">
                    {cat.items.map((item, iIdx) => (
                      <AccordionItem
                        key={iIdx}
                        value={`item-${cIdx}-${iIdx}`}
                        className="rounded-xl border border-border bg-card px-4 sm:px-6 py-1 shadow-sm"
                      >
                        <AccordionTrigger className="text-left font-headings text-base sm:text-lg font-bold text-primary hover:no-underline hover:text-accent">
                          {item.q}
                        </AccordionTrigger>
                        <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-2">
                          {item.a}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              ))}
            </div>

            {/* BROCHURE CTA */}
            <div className="mt-16 rounded-2xl bg-gradient-navy text-white p-8 lg:p-10 text-center shadow-premium border border-white/10">
              <h3 className="font-headings text-2xl sm:text-3xl font-bold mb-3">
                Ask Your Physician About Proliv®Rx Today
              </h3>
              <p className="text-light-blue/80 max-w-xl mx-auto text-sm sm:text-base mb-6">
                If you want your physician to know about Proliv®Rx, share our
                official clinical information brochure with them.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
                >
                  <a href="/#consultation">Book Free Consultation</a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="border-2 border-white/50 bg-transparent text-white hover:bg-white/15 hover:border-white/70"
                >
                  <a
                    href="https://prolivrx.com/wp-content/uploads/2026/06/MKTG-000030-Rev-2_ProlivRx_-Physician-Brochure_Digital-File-v2.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download Physician Brochure
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>
        <RelatedPages
          heading="Explore Our Treatments & Resources"
          links={[
            ...allTreatmentsLinks.filter((l) => l.href !== "/prolivrx"),
            ...allServiceLinks,
            ...allDecisionLinks,
          ]}
        />
      </main>

      <Footer />
    </div>
  );
}
